import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { IPublicoAlvoListItem } from 'app/domains/environment/publicos-alvo/interfaces/publico-alvo-request.interface';
import { IItemPublicoAlvoPayload } from '../../interfaces/item-publico-alvo.interface';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ApiError } from '../../../../../core/errors/api-error';
import { IModalSettings } from '../../../../shared/components/modal/modal.component';
import type { ISelectOption } from '../../../../shared/components/select/models/select-option.interface';
import { ToastService } from '../../../../shared/components/toast/services/toast.service';
import { ClassificacaoPLMApi } from '../../../classificacoes-plm/apis/classificacao-plm.api';
import { IClassificacaoPLMResponse } from '../../../classificacoes-plm/interfaces/classificacao-plm-response.interface';
import { IClassificacaoPLM } from '../../../classificacoes-plm/interfaces/classificacao-plm.interface';
import {
  EnumNucleoPLM,
  NUCLEO_PLM_DESCRIPTIONS,
} from '../../../plm/enums/nucleo-plm.enum';
import {
  EnumTipoEstrategicoPLM,
  TIPO_ESTRATEGICO_PLM_DESCRIPTIONS,
  TIPO_ESTRATEGICO_PLM_LABELS,
} from '../../../plm/enums/tipo-estrategico-plm.enum';
import { EnumFasePLM } from '../../../plm/enums/fase-plm.enum';
import {
  formatPercentInputValue,
  parsePercentInputValue,
} from '../../../../environment/shared/directives/percent-input/percent-input.util';
import { formatDecimalInputValue, parseDecimalInputValue } from '../../utils/decimal-input.util';
import { IGruposRentabilidadeResponse } from '../../../../core/settings/grupos-rentabilidade/interfaces/grupos-rentabilidade-response.interface';
import { DnaApi } from '../../../dnas/apis/dna.api';
import {
  IPopulacaoElegivel,
  PopulacaoElegivelService,
} from '../../../dnas/services/populacao-elegivel.service';
import { IDNAExpectativas } from '../../../dnas/interfaces/dna-expectativas.interface';
import { IDNAListItem } from '../../../dnas/interfaces/dna-request.interface';
import { IItemDNA } from '../../interfaces/item-dna.interface';
import { CAMPOS_PLM_EDITAVEIS, DATE_PLM_FIELDS, IPLM, PERCENT_PLM_FIELDS } from '../../interfaces/plm.interface';
import { listarParametrosMutados } from '../../utils/heranca-dna.util';
import {
  INovoDatepickerSettings,
  INovoDatepickerValue,
} from '../../../../shared/components/novo-datepicker/novo-datepicker-settings.interface';
import {
  extractDatepickerDate,
  parseLocalDate,
  toLocalDateString,
} from '../../../../shared/components/novo-datepicker/novo-datepicker.utils';
import { periodicidadeDiaria } from '../../../../../shared/periodicidade/periodicidade';

interface ISelectOptionWithDescription extends ISelectOption {
  description?: string;
}

interface IFaseMaturacao {
  controle: string;
  rotulo: string;
  rotuloMinusculo: string;
}

const TAXA_MAXIMA = 100;

type CampoDataPLM = typeof DATE_PLM_FIELDS[number];

const FASES_MATURACAO: IFaseMaturacao[] = [
  { controle: 'taxaMaturacaoNascimentoPLM', rotulo: 'Nascimento', rotuloMinusculo: 'nascimento' },
  { controle: 'taxaMaturacaoCrescimentoPLM', rotulo: 'Crescimento', rotuloMinusculo: 'crescimento' },
  { controle: 'taxaMaturacaoFortalecimentoPLM', rotulo: 'Fortalecimento', rotuloMinusculo: 'fortalecimento' },
  { controle: 'taxaMaturacaoMaturidadePLM', rotulo: 'Maturidade', rotuloMinusculo: 'maturidade' },
];

@Component({
  selector: 'sgt-plm-form',
  templateUrl: './plm-form.component.html',
  styleUrls: ['./plm-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PLMFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: IPLM | null = null;

  /**
   * Grupo de rentabilidade é um campo do item (não faz parte do payload do PLM).
   * É exibido junto ao DNA apenas visualmente; o valor pertence ao formulário pai.
   */
  @Input() idGrupoRentabilidade: number | null = null;
  @Input() exibirGrupoRentabilidade = false;
  @Input() podeEditarGrupoRentabilidade = false;
  @Output() idGrupoRentabilidadeChange = new EventEmitter<number>();

  @Input() dna: IItemDNA | null = null;
  @Input() gruposRentabilidade: IGruposRentabilidadeResponse[] = [];
  @Input() nomeItem = '';
  @Input() podeEditarDna = true;
  @Input() podeEditarDadosPLM = false;

  form: FormGroup;

  readonly valoresDatepicker: Partial<Record<CampoDataPLM, INovoDatepickerValue | null>> = {};

  get faseAtiva(): EnumFasePLM {
    return this.data?.fasePLM || EnumFasePLM.Nascimento;
  }

  get qtdParametrosDna(): number {
    const PARAMETROS_BASE = 6;
    return this.exibirGrupoRentabilidade ? PARAMETROS_BASE + 1 : PARAMETROS_BASE;
  }

  tipoEstrategicoPLM = [
    EnumTipoEstrategicoPLM.Inovacao,
    EnumTipoEstrategicoPLM.ComplementoLinha,
    EnumTipoEstrategicoPLM.Extensao,
    EnumTipoEstrategicoPLM.Reposicionamento,
  ].map((tipo) => ({
    id: tipo,
    label: TIPO_ESTRATEGICO_PLM_LABELS[tipo],
    description: TIPO_ESTRATEGICO_PLM_DESCRIPTIONS[tipo],
  }));

  readonly fasesMaturacao = FASES_MATURACAO;
  readonly passoTaxaPadrao = 5;

  classificacaoRAP: ISelectOptionWithDescription[] = [];
  classificacaoRAR: ISelectOptionWithDescription[] = [];

  descricaoTipoEstrategico = '';
  descricaoClassificacaoRAP = '';
  descricaoClassificacaoRAR = ''

  plmDescriptions = NUCLEO_PLM_DESCRIPTIONS;
  EnumNucleoPLM = EnumNucleoPLM;
  EnumTipoEstrategicoPLM = EnumTipoEstrategicoPLM;

  publicosAlvoSelecionados: IPublicoAlvoListItem[] = [];
  seletorPublicoAlvoAberto = false;

  valoresDoItemParaHeranca: Partial<IPLM> = {};

  dnaExpectativas: IDNAExpectativas | null = null;
  populacaoDoItem: IPopulacaoElegivel | null = null;
  seletorDnaAberto = false;
  desvincularDnaAberto = false;
  dnaEmTroca: number | null = null;

  readonly modalPublicoAlvoSettings: IModalSettings = {
    width: '560px',
    hasHeader: false,
    hasFooter: false,
    allowClickOutsideToClose: true,
    allowEscapeToClose: true,
  };

  readonly modalDnaSettings: IModalSettings = {
    width: '640px',
    hasHeader: false,
    hasFooter: false,
    allowClickOutsideToClose: true,
    allowEscapeToClose: true,
    customClass: 'plm-dna-modal-host',
  };

  readonly modalDesvincularSettings: IModalSettings = {
    width: '520px',
    hasHeader: false,
    hasFooter: false,
    allowClickOutsideToClose: true,
    allowEscapeToClose: true,
    customClass: 'plm-dna-modal-host',
  };

  datepickerSettings: Partial<INovoDatepickerSettings> = {
    periodicidades: ['diaria'],
    disabled: true,
  };

  readonly datepickerDerivadoSettings: Partial<INovoDatepickerSettings> = {
    periodicidades: ['diaria'],
    disabled: true,
  };

  readonly periodicidadeDiariaId = periodicidadeDiaria.id;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private classificacaoPLMApi: ClassificacaoPLMApi,
    private dnaApi: DnaApi,
    private populacaoService: PopulacaoElegivelService,
    private toastService: ToastService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dna?.currentValue) {
      this.adotarGrupoRentabilidadeDoDna();
    }

    if (changes.podeEditarDadosPLM && this.form) {
      this.aplicarPermissaoDeEdicao();
    }
  }

  ngOnInit() {
    this.populacaoService.resetar();
    this.createForm();
    this.aplicarPermissaoDeEdicao();
    this.fetchClassificacoesRAP();

    if (this.dna) {
      this.carregarParametrosDna(this.dna.id);
    }

    if (this.data) {
      const { publicosAlvo, ...dadosForm } = this.data;
      this.form.patchValue(dadosForm);
      DATE_PLM_FIELDS.forEach((campo) => this.sincronizarValorDatepicker(campo));
      PERCENT_PLM_FIELDS.forEach((field) =>
        this.form.get(field).setValue(formatPercentInputValue(this.data[field]))
      );
      this.form.get('pesoMedioMaturacaoPLM').setValue(formatDecimalInputValue(this.data.pesoMedioMaturacaoPLM, 6));

      if (this.data.tipoEstrategicoPLM) {
        this.descricaoTipoEstrategico = this.tipoEstrategicoPLM.find(t => t.id === this.data.tipoEstrategicoPLM)?.description || '';
      }

      this.publicosAlvoSelecionados = (publicosAlvo ?? [])
        .filter(vinculo => vinculo.idPublicoAlvo && vinculo.publicoAlvo?.nome)
        .map(vinculo => ({
          id: vinculo.idPublicoAlvo,
          nome: vinculo.publicoAlvo.nome,
        }));
      this.sincronizarControlePublicosAlvo();
    }

    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const tipoEstrategicoControl = this.form.get('tipoEstrategicoPLM');
      if (tipoEstrategicoControl?.value?.id) {
        this.descricaoTipoEstrategico = tipoEstrategicoControl.value.description || '';
        tipoEstrategicoControl.setValue(tipoEstrategicoControl.value.id, { emitEvent: false });
      } else {
        this.descricaoTipoEstrategico =
          this.tipoEstrategicoPLM.find((t) => t.id === tipoEstrategicoControl?.value)?.description || '';
      }

      const rapControl = this.form.get('idClassificacaoPLMRAP');
      if (rapControl?.value?.id) {
        this.descricaoClassificacaoRAP = rapControl.value.description || '';
        rapControl.setValue(rapControl.value.id, { emitEvent: false });
      }

      const rarControl = this.form.get('idClassificacaoPLMRAR');
      if (rarControl?.value?.id) {
        this.descricaoClassificacaoRAR = rarControl.value.description || '';
        rarControl.setValue(rarControl.value.id, { emitEvent: false });
      }

      this.atualizarValoresParaHeranca();
    });

    this.atualizarValoresParaHeranca();
    this.observarPopulacaoDosPublicosAlvo();
  }

  private observarPopulacaoDosPublicosAlvo(): void {
    this.populacaoService
      .observar()
      .pipe(takeUntil(this.destroy$))
      .subscribe((populacao) => {
        this.populacaoDoItem = populacao;
        this.cdr.markForCheck();
      });

    this.populacaoService
      .observarFalhas()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() =>
        this.toastService.error('Erro ao medir a população dos públicos-alvo.')
      );
  }

  createForm(): void {
    this.form = this.fb.group({
      nucleoPLM: [{ value: null, disabled: true }],
      fasePLM: [{ value: null, disabled: true }],
      cicloPLM: [{ value: null, disabled: true }],
      tipoEstrategicoPLM: null,
      idClassificacaoPLMRAP: null,
      idClassificacaoPLMRAR: [{ value: null, disabled: true }],
      dataPesquisaPLM: null,
      dataDesenvolvimentoPLM: null,
      dataGoToMarketPLM: null,
      dataNascimentoPLM: null,
      dataEncerramentoPLM: null,
      publicosAlvo: [[]],
      idDNAPLM: null,
      taxaAdocaoPLM: null,
      pesoMedioMaturacaoPLM: null,
      taxaMaturacaoNascimentoPLM: null,
      taxaMaturacaoCrescimentoPLM: null,
      taxaMaturacaoFortalecimentoPLM: null,
      taxaMaturacaoMaturidadePLM: null,
    });
  }

  fetchClassificacoesRAP(): void {
    this.classificacaoPLMApi.fetchList().pipe(take(1)).subscribe({
      next: (response: IClassificacaoPLMResponse) => {
        if (response.data) {
          this.classificacaoRAP = response.data.map((item: IClassificacaoPLM) => ({
            id: item.id,
            label: `${item.codigo} - ${item.nome}`,
            isSelected: this.data?.idClassificacaoPLMRAP === item.id,
            description: item.descricao,
          }));
          this.classificacaoRAR = response.data.map((item: IClassificacaoPLM) => ({
            id: item.id,
            label: `${item.codigo} - ${item.nome}`,
            isSelected: this.data?.idClassificacaoPLMRAR === item.id,
            description: item.descricao,
          }));

          if (this.data?.idClassificacaoPLMRAP) {
            this.descricaoClassificacaoRAP = this.classificacaoRAP.find(c => c.id === this.data.idClassificacaoPLMRAP)?.description || '';
          }
          if (this.data?.idClassificacaoPLMRAR) {
            this.descricaoClassificacaoRAR = this.classificacaoRAR.find(c => c.id === this.data.idClassificacaoPLMRAR)?.description || '';
          }
        }
      },
      error: (error: ApiError) => {
        this.toastService.error(error.message, {
          title: error.cause || 'Erro ao buscar classificações RAP',
        });
        console.error('Erro ao buscar classificações RAP:', error);
      }
    })
  }

  passoTaxa(controle: string, delta: number): void {
    const campo = this.form.get(controle);
    if (campo.disabled) {
      return;
    }
    const atual = parsePercentInputValue(campo.value) ?? 0;
    const limitado = Math.min(Math.max(atual + delta, 0), TAXA_MAXIMA);
    const proximo = Math.round(limitado * 100) / 100;
    campo.setValue(String(proximo).replace('.', ','));
    campo.markAsTouched();
  }

  passoPesoMedio(delta: number): void {
    const campo = this.form.get('pesoMedioMaturacaoPLM');
    if (campo.disabled) {
      return;
    }
    const atual = parseDecimalInputValue(campo.value) ?? 0;
    const proximo = Math.round(Math.max(0, atual + delta) * 1e6) / 1e6;
    campo.setValue(String(proximo).replace('.', ','));
    campo.markAsTouched();
  }

  tamanhoInput(controle: string): number {
    return Math.max(2, String(this.form?.get(controle)?.value ?? '').length);
  }

  campoDesabilitado(controle: string): boolean {
    return !!this.form?.get(controle)?.disabled;
  }

  trackFaseByControle(_index: number, fase: IFaseMaturacao): string {
    return fase.controle;
  }

  private sincronizarValorDatepicker(campo: CampoDataPLM): void {
    const date = this.form.get(campo)?.value;
    this.valoresDatepicker[campo] = date
      ? { date: toLocalDateString(new Date(date)), periodicidade: periodicidadeDiaria.id }
      : null;
  }

  onChangeData(campo: CampoDataPLM, valor: INovoDatepickerValue | null): void {
    const isoDate = extractDatepickerDate(valor);
    this.form.get(campo).setValue(isoDate ? parseLocalDate(isoDate) : null);
    this.sincronizarValorDatepicker(campo);
  }

  get idsPublicosAlvoSelecionados(): number[] {
    return this.publicosAlvoSelecionados.map(p => p.id);
  }

  abrirSeletorPublicoAlvo(): void {
    this.seletorPublicoAlvoAberto = true;
    this.cdr.markForCheck();
  }

  fecharSeletorPublicoAlvo(): void {
    this.seletorPublicoAlvoAberto = false;
    this.cdr.markForCheck();
  }

  onPublicosAlvoSelecionados(publicosAlvo: IPublicoAlvoListItem[]): void {
    this.publicosAlvoSelecionados = publicosAlvo;
    this.sincronizarControlePublicosAlvo();
    this.fecharSeletorPublicoAlvo();
  }

  removerPublicoAlvo(id: number): void {
    this.publicosAlvoSelecionados = this.publicosAlvoSelecionados.filter(p => p.id !== id);
    this.sincronizarControlePublicosAlvo();
    this.cdr.markForCheck();
  }

  trackPublicoAlvoById(_index: number, item: IPublicoAlvoListItem): number {
    return item.id;
  }

  get parametrosMutados(): string {
    return listarParametrosMutados(this.valoresDoItemParaHeranca, this.dna).join(', ');
  }

  abrirSeletorDna(troca = false): void {
    this.dnaEmTroca = troca ? this.dna?.id ?? null : null;
    this.seletorDnaAberto = true;
    this.cdr.markForCheck();
  }

  fecharSeletorDna(): void {
    this.seletorDnaAberto = false;
    this.dnaEmTroca = null;
    this.cdr.markForCheck();
  }

  onDnaSelecionado(escolhido: IDNAListItem): void {
    const eraTroca = this.dnaEmTroca !== null;
    this.form.get('idDNAPLM').setValue(escolhido.id);
    this.form.get('idDNAPLM').markAsDirty();
    this.fecharSeletorDna();
    this.carregarParametrosDna(escolhido.id);

    this.toastService.success(
      eraTroca
        ? `SKU movido para "${escolhido.nome}". Salve o item para confirmar.`
        : `SKU vinculado a "${escolhido.nome}". Salve o item para confirmar.`
    );
  }

  abrirDesvincularDna(): void {
    this.desvincularDnaAberto = true;
    this.cdr.markForCheck();
  }

  fecharDesvincularDna(): void {
    this.desvincularDnaAberto = false;
    this.cdr.markForCheck();
  }

  onDesvincularDna(): void {
    this.form.get('idDNAPLM').setValue(null);
    this.form.get('idDNAPLM').markAsDirty();
    this.dna = null;
    this.dnaExpectativas = null;
    this.fecharDesvincularDna();

    this.toastService.success(
      'Vínculo removido — o SKU deixa de ser avaliado pelo PLM. Salve o item para confirmar.'
    );
  }

  onAbrirDna(id: number): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/configuracoes/dnas', id, 'editar'])
    );
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private atualizarValoresParaHeranca(): void {
    const valores = this.form.getRawValue();
    this.valoresDoItemParaHeranca = {
      ...valores,
      ...PERCENT_PLM_FIELDS.reduce(
        (acumulado, campo) => ({ ...acumulado, [campo]: parsePercentInputValue(valores[campo]) }),
        {}
      ),
      pesoMedioMaturacaoPLM: parseDecimalInputValue(valores.pesoMedioMaturacaoPLM),
    };
  }

  private aplicarPermissaoDeEdicao(): void {
    this.datepickerSettings = {
      periodicidades: ['diaria'],
      disabled: !this.podeEditarDadosPLM,
    };

    CAMPOS_PLM_EDITAVEIS.forEach((campo) => {
      const controle = this.form.get(campo);
      if (!controle) {
        return;
      }
      if (this.podeEditarDadosPLM) {
        controle.enable({ emitEvent: false });
      } else {
        controle.disable({ emitEvent: false });
      }
    });
    this.cdr.markForCheck();
  }

  private carregarParametrosDna(id: number): void {
    this.dnaApi
      .fetchParametros(id)
      .pipe(take(1))
      .subscribe({
        next: ({ expectativas, ...parametros }) => {
          this.dna = parametros;
          this.dnaExpectativas = expectativas ?? null;
          this.adotarGrupoRentabilidadeDoDna();
          this.cdr.markForCheck();
        },
        error: (error: ApiError) => {
          this.toastService.error(error.message, {
            title: error.cause || 'Erro ao buscar os parâmetros do DNA',
          });
        },
      });
  }

  onChangeGrupoRentabilidade(id: number | null): void {
    if (!this.podeEditarDadosPLM) {
      return;
    }

    this.idGrupoRentabilidade = id;
    this.idGrupoRentabilidadeChange.emit(id);

    if (!id) {
      this.adotarGrupoRentabilidadeDoDna();
    }
  }

  private adotarGrupoRentabilidadeDoDna(): void {
    const herdado = this.dna?.idGrupoRentabilidade;
    if (!herdado || this.idGrupoRentabilidade) {
      return;
    }

    this.idGrupoRentabilidade = herdado;
    this.idGrupoRentabilidadeChange.emit(herdado);
    this.cdr.markForCheck();
  }

  private sincronizarControlePublicosAlvo(): void {
    const payload: IItemPublicoAlvoPayload[] = this.publicosAlvoSelecionados.map(p => ({
      idPublicoAlvo: p.id,
    }));
    this.form.get('publicosAlvo')?.setValue(payload, { emitEvent: false });
    this.atualizarValoresParaHeranca();
    this.populacaoService.medir(this.idsPublicosAlvoSelecionados);
  }
}
